import { EVENTS, subscribe, publish } from 'ia-util-pubsub';
import { reviveScripts } from 'ia-util-helpers';

/**
 * Rendered areas of this visit, keyed by the address that produced them. Module
 * scope on purpose: the component that fills them lives inside the area, so it
 * is replaced on every switch, and a per-instance store would be thrown away
 * exactly when it is about to be useful.
 */
const renderedAreas = new Map();
// Shortest time the Load More spinner stays up once shown.
const LOAD_MORE_BUSY_MS = 400;
// Bounded on purpose: every address the shopper visits leaves a detached copy
// of a whole results area here, and a long filtering session would otherwise
// keep hundreds of them alive. Oldest goes first, which is also the least
// likely to be returned to.
const MAX_REMEMBERED_AREAS = 6;
let lastRenderedUrl = null;
// A cell is a product led by one variant (a variant collection opens it on a listed size).
const cellKey = (cell) => `${cell.dataset.productId}:${cell.dataset.variantId}`;

if (!customElements.get("ia-filter-component")) {
  class IaFilterComponent extends HTMLElement {
    connectedCallback() {
      // The server-rendered area is a rendered area too: without claiming it,
      // coming back to the tab the page opened on rebuilt it from scratch.
      // ??=, not ||=: on a page with no query string the claim is "", which is
      // falsy but a perfectly valid cache key, and ||= dropped exactly the
      // unfiltered grid that Clear all comes back to.
      lastRenderedUrl ??= window.location.search;
      this.addEventListener("change", (evt) => {
        // The engine answers for the filter FORM only. The drawer can also hold
        // groups a page brought itself (the search page's blog tab reorders and
        // hides cells it has already rendered); those live outside the form and
        // must not trigger a server round-trip, which would refetch the area and
        // wipe the box that was just ticked.
        if (!evt.target?.closest?.('form[data-ia-filter-form], form[data-ia-sort-form]')) return;

        const fromPriceChange = evt.target?.closest?.('[data-ia-price-range-input]');
        if (fromPriceChange) {
          const { ok, changed } = this.normalizePriceInputs();
    
          if (!ok) {
            // Still invalid (e.g., FROM > TO with one empty), do not submit.
            return;
          }
    
          if (changed) {
            // Values were auto-corrected this tick (e.g., TO set to FROM, or clamped).
            // Let the DOM reflect the new values, then submit once.
            queueMicrotask(() => this.onApplyFilters(evt));
            return;
          }
          // ok && !changed → already valid, fall through to immediate submit.
        }
    
        this.onApplyFilters(evt);
      });

      // Bound once and released on disconnect: the drawer teleports to <body>
      // and re-runs connectedCallback, and an unreleased copy per connect made
      // one price change fire N submits.
      this._onPriceRange ||= (event) => {
        const { minValue, maxValue } = event.detail;
        const minInput = this.querySelector('[data-ia-price-range-input="min"]');
        const maxInput = this.querySelector('[data-ia-price-range-input="max"]');

        if (minInput && minValue !== undefined)
          minInput.value = minValue > 0 ? minValue : "";
        if (maxInput && maxValue !== undefined) {
          const maxBound = parseFloat(maxInput.max);
          maxInput.value = maxValue < maxBound ? maxValue : "";
        }
      };
      document.removeEventListener("price-range-change", this._onPriceRange);
      document.addEventListener("price-range-change", this._onPriceRange);

      this.subscribeToEvents();
      this.watchDrawer();
    }

    /**
     * The drawer declares role="dialog" + aria-modal but the shared component
     * never moves focus in. Added here only, by watching its is-open class.
     */
    watchDrawer() {
      this.drawer = this.closest('[data-drawer-id]');
      if (!this.drawer) return;

      this.wasOpen = this.drawer.classList.contains('is-open');
      this.drawerObserver = new MutationObserver(() => {
        const isOpen = this.drawer.classList.contains('is-open');
        if (isOpen === this.wasOpen) return;
        this.wasOpen = isOpen;
        isOpen ? this.onDrawerOpened() : this.onDrawerClosed();
      });
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

      this._boundTrapFocus = this.trapFocus.bind(this);
      this.drawer.addEventListener('keydown', this._boundTrapFocus);
    }

    disconnectedCallback() {
      this.drawerObserver?.disconnect();
      if (this._boundTrapFocus) this.drawer?.removeEventListener('keydown', this._boundTrapFocus);
      if (this._onPriceRange) document.removeEventListener("price-range-change", this._onPriceRange);
      this.busHandles?.forEach((off) => off?.());
      this.busHandles = null;
    }

    onDrawerOpened() {
      this.returnFocusTo = document.activeElement;
      // Every opening starts at the top of the list. Scrolled halfway down and
      // closed, it used to reopen exactly there, with the first groups out of
      // sight and nothing to say why. Done here, while the panel is still off
      // screen, so the reset is never a visible jump.
      this.drawer.querySelectorAll('.ia-drawer__window').forEach((pane) => {
        pane.scrollTop = 0;
      });
      // The dialog takes focus so its name is read before its contents.
      this.drawer.setAttribute('tabindex', '-1');
      // The drawer animates in; focusing a still-hidden element does nothing.
      requestAnimationFrame(() => this.drawer.focus({ preventScroll: true }));
    }

    onDrawerClosed() {
      this.drawer.removeAttribute('tabindex');
      this.returnFocusTo?.focus?.({ preventScroll: true });
      this.returnFocusTo = null;
    }

    getFocusable() {
      return [...this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((el) => el.offsetParent !== null || el === document.activeElement);
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = this.getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.drawer)) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    /** The pinned bar of THIS drawer, through the drawer->bar seam the Liquid
        layer already provides (`data-filter-bar="<drawer id>"`). */
    get bar() {
      const barId = (this.drawer || this.closest('[data-drawer-id]'))?.dataset.drawerId;
      return barId ? document.querySelector(`[data-filter-bar="${CSS.escape(barId)}"]`) : null;
    }

    /** The section this drawer answers for. The drawer teleports to <body>, so
        closest() cannot reach the section from here, but the bar stays put in
        it; a bar-less embed falls back to the whole document. */
    get scope() {
      return this.bar?.closest('[id^="shopify-section-"]') || document;
    }

    getProductsArea() {
      return this.scope.querySelector('[data-ia-products-area]');
    }

    subscribeToEvents() {
      // Handles are kept because this component now lives INSIDE the area it
      // renders: every tab switch replaces the instance, and a subscription
      // that outlived its element kept writing into the page. Two of those
      // racing put the article markup under the products tab.
      this.busHandles = [
        subscribe(EVENTS.loadingStateChange, (event) => this.handleLoadingStateChange(event.detail)),
        subscribe(EVENTS.filtersUpdate, (event) => this.handleFiltersUpdate(event.detail)),
        subscribe(EVENTS.filtersError, (event) => this.handleError(event.detail)),
      ];
    }

    handleLoadingStateChange({ isLoading }) {
      this.toggleLoading(isLoading);
    }

    handleFiltersUpdate({ html, append }) {
      if (html) {
        this.renderProductsArea(html, append);
        
        if (!append) {
          this.renderFilterHTML(html);
          this.updateProductCount(html);
          this.scrollToResults();
        }
      }
    }

    /** A filtered grid is a different list, so reading it starts at its top.
     *  Load More is exempt (append), the shopper is mid-list on purpose there.
     *  Only scrolls UP: standing above the grid already, the answer is in
     *  sight and yanking the page down would be the surprise instead. */
    scrollToResults() {
      const area = this.scope.querySelector('[data-ia-products-area]') || this.getProductGrid?.();
      if (!area) return;
      const top = area.getBoundingClientRect().top + window.scrollY - this.stickyOffset();
      if (window.scrollY <= top) return;
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
    }

    /** Whatever is pinned over the grid: the header bar, plus the filter row
     *  when it sticks. Read live, both heights change with the viewport. */
    stickyOffset() {
      const header = parseFloat(getComputedStyle(document.documentElement)
        .getPropertyValue('--ia-header-height')) || 0;
      const bar = this.scope.querySelector('.ia-filter-bar');
      const barHeight = bar && getComputedStyle(bar).position === 'sticky'
        ? bar.getBoundingClientRect().height
        : 0;
      return header + barHeight;
    }
    
    /** Only digits change on screen; a live region with the whole sentence is what gets read. */
    announceCount(count, isActive) {
      const status = this.scope.querySelector('[data-filter-status]');
      if (!status) return;
      status.textContent = isActive
        ? `${count} products, filters applied`
        : `${count} products`;
    }

    /** The section's bar and the drawer's own header: the drawer is teleported
        to <body>, so the scope alone misses the count over this component. */
    counters(selector) {
      const drawer = this.closest('ia-drawer-component');
      const inDrawer = drawer && !this.scope.contains(drawer) ? [...drawer.querySelectorAll(selector)] : [];
      return [...this.scope.querySelectorAll(selector), ...inDrawer];
    }

    updateProductCount(html) {
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      const newCountElement = parsedDom.querySelector('[data-filter-count]');
      const countElements = this.counters('[data-filter-count]');
      
      if (newCountElement && countElements.length > 0) {
        const count = newCountElement.textContent.trim();
        // The "filters applied" dot rides on .is-active, so sync it too.
        const isActive = newCountElement.classList.contains('is-active');
        countElements.forEach(el => {
          el.textContent = count;
          el.classList.toggle('is-active', isActive);
        });
        // The sticky-bar dot rides on the label, not the count.
        this.counters('[data-filter-active]').forEach(el => {
          el.classList.toggle('is-active', isActive);
        });
        this.announceCount(count, isActive);
      }

      this.updateActivePills(parsedDom);
    }

    // The fetched section HTML contains the whole bar, so swap the pills in.
    updateActivePills(parsedDom) {
      const newPills = parsedDom.querySelector('[data-filter-pills]');
      if (!newPills) return;
      const hasPills = !!newPills.querySelector('[data-remove-filter]');
      this.scope.querySelectorAll('[data-filter-pills]').forEach(el => {
        el.innerHTML = newPills.innerHTML;
        el.classList.toggle('is-empty', !hasPills);
      });
    }
    
    handleError({ error }) {
      console.error('Component Error:', error);
    }

    toggleLoading(isLoading = true) {
      const productsArea = this.getProductsArea();
      if (!productsArea) return;

      // Stops assistive tech reading a half-updated list.
      productsArea.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      // The bar carries the same state: its pills dim and stop taking taps, so
      // a removal that is still in flight does not read as a tap that missed.
      this.scope.querySelectorAll('.ia-filter-bar').forEach((bar) => {
        bar.toggleAttribute('data-filters-busy', isLoading);
      });

      if (isLoading) {
        // Holds the height while the request is in flight, or the viewport jumps.
        productsArea.style.minHeight = `${productsArea.offsetHeight}px`;
        this.classList.add('loading');
        productsArea.classList.add('loading');
        if (this.applyButton) this.applyButton.disabled = true;
        
      } else {
        productsArea.style.minHeight = '';
        this.classList.remove('loading');
        productsArea.classList.remove('loading');
        if (this.applyButton) this.applyButton.disabled = false;
      }
    }

    /**
     * Remembers what is on screen so it can be put back. A COPY, not the nodes
     * themselves: the reconcile below moves surviving cells into the incoming
     * area, and a fragment of live nodes would be quietly emptied of exactly
     * the products the shopper is coming back to (measured: Back after a sort
     * restored a grid holding only the promo tiles).
     */
    stashArea(productsArea) {
      if (!lastRenderedUrl) return;
      const kept = document.createDocumentFragment();
      [...productsArea.childNodes].forEach((node) => kept.append(node.cloneNode(true)));
      renderedAreas.set(lastRenderedUrl, kept);
      while (renderedAreas.size > MAX_REMEMBERED_AREAS) {
        renderedAreas.delete(renderedAreas.keys().next().value);
      }
    }

    renderProductsArea(html, append = false) {
      const productsArea = this.getProductsArea();
      if (!productsArea) return false;

      const url = window.location.search;
      if (!append && url !== lastRenderedUrl) {
        const remembered = renderedAreas.get(url);
        if (remembered) {
          this.stashArea(productsArea);
          // Copies again, so the memory survives being used more than once.
          productsArea.replaceChildren(...[...remembered.childNodes].map((node) => node.cloneNode(true)));
          lastRenderedUrl = url;
          return true;
        }
      }
      
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      // Not scoped to the collection section any more: the fetch answers with ONE
      // section (verified: a single [data-ia-products-area] in the fragment), and
      // the search page needs the same engine under a different wrapper.
      const newProductsArea = parsedDom.querySelector('[data-ia-products-area]');
      
      if (!newProductsArea) return false;
      
      if (append) {
        // Append mode: append new products to existing grid
        const productGrid = productsArea.querySelector('[data-ia-product-grid]');
        const newProductGrid = newProductsArea.querySelector('[data-ia-product-grid]');
        
        if (!productGrid || !newProductGrid) return false;
        
        const newProducts = newProductGrid.querySelectorAll('.ia-collection-grid__product');
        if (newProducts.length === 0) return false;
        
        const mountedKeys = new Set(
          Array.from(productGrid.querySelectorAll(':scope > [data-product-id]'), cellKey)
        );
        newProducts.forEach(item => {
          if (!mountedKeys.has(cellKey(item))) {
            productGrid.appendChild(item.cloneNode(true));
          }
        });
      } else {
        // Replace mode: replace entire products area
        // Update collection data (it's outside products area, so needs separate update)
        const newCollectionData = parsedDom.querySelector('[data-ia-collection-data]');
        const currentCollectionData = this.scope.querySelector('[data-ia-collection-data]');
        if (newCollectionData && currentCollectionData) {
          currentCollectionData.textContent = newCollectionData.textContent;
        }
        
        const newProductsAreaClone = newProductsArea.cloneNode(true);

        // Remembered BEFORE the reconcile below, because the reconcile moves the
        // surviving cells out of this area and a copy taken afterwards would be
        // a grid of promo tiles with no products in it (measured: Back after a
        // filter change restored exactly that).
        this.stashArea(productsArea);

        // A product that survives the change keeps its NODE, and with it its
        // decoded images: rebuilding the grid re-created every <img> and the
        // whole thing blinked on a filter that changed two cards. Same trick as
        // the cart's line reconcile, keyed by product and variant id.
        const mounted = new Map();
        productsArea.querySelectorAll('[data-ia-product-grid] > [data-product-id]').forEach((cell) => {
          mounted.set(cellKey(cell), cell);
        });
        newProductsAreaClone.querySelectorAll('[data-ia-product-grid] > [data-product-id]').forEach((fresh) => {
          const keep = mounted.get(cellKey(fresh));
          // Style carries the batch reveal, so the fresh copy's decision wins.
          if (keep) {
            keep.style.cssText = fresh.style.cssText;
            fresh.replaceWith(keep);
          }
        });

        // Nodes, not markup: innerHTML would serialise the survivors back into
        // strings and undo the move above.
        productsArea.replaceChildren(...newProductsAreaClone.childNodes);
        // An embed cell's widget script came through the parser and never ran.
        productsArea.querySelectorAll('[data-ia-embed]').forEach(reviveScripts);
      }
      
      lastRenderedUrl = url;
      return true;
    }

    renderFilterHTML(html) {
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');

      // Replacing the markup drops focus to <body>; name+value finds the input again.
      const active = document.activeElement;
      const focusTarget = active && active.name && this.contains(active)
        ? { name: active.name, value: active.value }
        : null;

      // The pinned Clear all is server-shaped (a link for Shopify's filters, a
      // button for page-owned pills), and its address moves with the filters: a
      // client-side transition must hand over the fresh one, or the blog tab
      // keeps the products link and tapping it navigates back to that search.
      const mySecondary = this.bar?.querySelector('.ia-filter-bar__secondary');
      const newSecondary = parsedDom.querySelector('.ia-filter-bar__secondary');
      if (mySecondary && newSecondary) {
        mySecondary.className = newSecondary.className;
        const myClear = mySecondary.querySelector('.ia-filter-bar__clear-top');
        const newClear = newSecondary.querySelector('.ia-filter-bar__clear-top');
        if (myClear && newClear) myClear.replaceWith(newClear.cloneNode(true));
      }

      // Update filter groups
      const currentFilterGroups = this.querySelectorAll('.ia-filter-group__content');
      const newFilterGroups = parsedDom.querySelectorAll('.ia-filter-group__content');

      // Patching group by group keeps focus and scroll where the shopper left
      // them, but it only means anything while the two sides describe the SAME
      // groups. The kind attribute says WHO answers them (Shopify's form or the
      // page itself): a kind change is always a different drawer, and counting
      // groups alone once matched the blog drawer against products-with-one-
      // filter (two groups each). Same kind with a different count still
      // replaces: patch-by-index would stitch mismatched groups together.
      const body = this.querySelector('.ia-filter-bar__body') || this;
      const newBody = parsedDom.querySelector('.ia-filter-bar__body');
      const kindChanged = newBody && newBody.dataset.filterKind !== this.dataset.filterKind;
      if (newBody && (kindChanged || currentFilterGroups.length !== newFilterGroups.length)) {
        this.dataset.filterKind = newBody.dataset.filterKind;
        body.replaceChildren(...newBody.cloneNode(true).childNodes);
        return;
      }

      currentFilterGroups.forEach((currentGroup, index) => {
        if (newFilterGroups[index]) {
          currentGroup.innerHTML = newFilterGroups[index].innerHTML;
        }
      });

      // Refreshes the selected-values summary next to each label.
      const currentTitles = this.querySelectorAll('.ia-accordion-title');
      const newTitles = parsedDom.querySelectorAll('.ia-accordion-title');
      currentTitles.forEach((currentTitle, index) => {
        if (newTitles[index]) {
          currentTitle.innerHTML = newTitles[index].innerHTML;
        }
      });

      // Update mobile footer
      this.renderMobileFooter(parsedDom);

      this.restoreFocus(focusTarget);
    }

    restoreFocus(target) {
      if (!target) return;
      const selector = `[name="${CSS.escape(target.name)}"][value="${CSS.escape(target.value)}"]`;
      // preventScroll: focus must not yank the drawer's scroll position.
      this.querySelector(selector)?.focus({ preventScroll: true });
    }
    
    renderMobileFooter(parsedDom) {
      // Both surfaces put the footer inside this drawer's own content.
      const currentFooter = this.querySelector('.ia-extra-option__footer');
      const newFooter = parsedDom.querySelector('.ia-extra-option__footer');
      
      if (currentFooter && newFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }
    }

    onApplyFilters(event) {
      event.preventDefault();

      const localForms = this.querySelectorAll(
        "form[data-ia-filter-form], form[data-ia-sort-form]",
      );

      const hasFilterForm = Array.from(localForms).some(form => form.hasAttribute('data-ia-filter-form'));
      const hasSortForm = Array.from(localForms).some(form => form.hasAttribute('data-ia-sort-form'));

      const allForms = [...localForms];
      
      // A kind this drawer does not carry is borrowed from its own section
      // first; the document is the last resort for a surface that keeps the
      // missing form in another drawer (drawers live in <body>, outside scope).
      if (!hasFilterForm) {
        const filterForm = this.scope.querySelector('form[data-ia-filter-form]')
          || document.querySelector('form[data-ia-filter-form]');
        if (filterForm) {
          allForms.push(filterForm);
        }
      }

      if (!hasSortForm) {
        const sortForm = this.scope.querySelector('form[data-ia-sort-form]')
          || document.querySelector('form[data-ia-sort-form]');
        if (sortForm) {
          allForms.push(sortForm);
        }
      }

      publish(EVENTS.filtersApply, { detail: { forms: allForms } });
    }

    normalizePriceInputs() {
      const from = this.querySelector('[data-ia-price-range-input="min"]');
      const to   = this.querySelector('[data-ia-price-range-input="max"]');
    
      const num = (v) => (v === '' ? NaN : Number(v));
    
      const clamp = (el) => {
        if (!el) return { changed:false };
        // ensure native constraints are honored even if theme HTML used type="text"
        if (el.type !== 'number') el.type = 'number';
        el.inputMode = 'numeric';
    
        const min = el.min !== '' ? Number(el.min) : -Infinity;
        const max = el.max !== '' ? Number(el.max) :  Infinity;
        const before = el.value;
        let v = num(el.value);
    
        if (!Number.isNaN(v)) {
          if (v < min) v = min;
          if (v > max) v = max;
          el.value = String(v);
        } else if (el.value !== '') {
          // invalid (e.g., letters) → clear
          el.value = '';
        }
        return { changed: el.value !== before };
      };
    
      const c1 = clamp(from);
      const c2 = clamp(to);
    
      // keep FROM ≤ TO (only when both are numbers)
      const a = num(from?.value ?? '');
      const b = num(to?.value ?? '');
      let c3 = false;
      // No change event from here: this runs inside the change handler, which
      // submits on `changed` itself, and a dispatched one would re-enter it.
      if (!Number.isNaN(a) && !Number.isNaN(b) && a > b && to) {
        to.value = String(a);
        c3 = true;
      }
    
      // Decide if it's safe to submit:
      // - OK if both are empty, or one is empty, or both are numbers AND a ≤ b.
      // - Not OK if any normalization changed the values this tick (we'll let the UI update first).
      const changedThisTick = (c1?.changed || c2?.changed || c3);
      const bothNums = !Number.isNaN(a) && !Number.isNaN(b);
      const ok =
        (!from || from.value === '') ||
        (!to || to.value === '') ||
        (bothNums && a <= b);
      
      return { ok, changed: changedThisTick };
    }
  }

  customElements.define("ia-filter-component", IaFilterComponent);
}


class IaLoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this._boundLoadMore = this.loadMore.bind(this);
    this._boundHandleFiltersUpdate = this.handleFiltersUpdate.bind(this);
    this._boundIntersectionCallback = this.handleIntersection.bind(this);
    this.isLoading = false;
    this.observer = null;
  }

  connectedCallback() {
    this.button?.addEventListener('click', this._boundLoadMore);
    
    this.unsubscribeFiltersUpdate = subscribe(EVENTS.filtersUpdate, this._boundHandleFiltersUpdate);
    this.unsubscribeLoadingState = subscribe(EVENTS.loadingStateChange, (event) => {
      this.handleLoadingStateChange(event.detail);
    });

    this.setupScrollMode();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this._boundLoadMore);
    this.unsubscribeFiltersUpdate?.();
    this.unsubscribeLoadingState?.();
    
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  setupScrollMode() {
    const mode = this.getAttribute('mode');
    if (mode === 'scroll') {
      this.observer = new IntersectionObserver(this._boundIntersectionCallback, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      });
      
      this.observer.observe(this);
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];
    
    if (entry.isIntersecting && !this.isLoading) {
      this.loadMore();
    }
  }

  handleLoadingStateChange({ isLoading }) {
    this.isLoading = isLoading;
    if (this.button) {
      this.button.disabled = isLoading;
    }
  }

  loadMore() {
    if (this.isLoading) {
      return;
    }

    publish(EVENTS.loadMoreRequested, {});
  }

  handleFiltersUpdate(event) {
    const { currentPage, totalPages, append } = event.detail;

    if (append === false) {
      this.classList.add('hidden');
      return;
    }

    if (totalPages === null) {
      this.classList.remove('hidden');
    } else if (currentPage >= totalPages) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
  }
}

if (!customElements.get('ia-load-more-button')) {
  customElements.define('ia-load-more-button', IaLoadMoreButton);
}

class IaPaginationComponent extends HTMLElement {
  constructor() {
    super();
    this.config = {
      productsPerBatch: 12,
      currentPage: 1,
      totalProducts: 0
    };
    this.pageItems = [];
    this.observer = null;
  }

  connectedCallback() {
    this.addEventListener('click', this.handlePageClick.bind(this), true);
    this.unsubscribeFiltersUpdate = subscribe(EVENTS.filtersUpdate, this.handleFiltersUpdate.bind(this));
    this.unsubscribeLoadingState = subscribe(EVENTS.loadingStateChange, (event) => {
      this.setLoadMoreBusy(event.detail.isLoading);
    });
    this.init();
  }

  disconnectedCallback() {
    this.unsubscribeFiltersUpdate?.();
    this.unsubscribeLoadingState?.();
    this.observer?.disconnect();
    clearTimeout(this._busyTimer);
  }

  // === INITIALIZATION ===

  init() {
    this.loadConfig();
    this.mapProducts();
    this.setupObserver();
    this.updateUI();
  }

  /** This button lives in the section's own markup, so closest() reaches it. */
  get scope() {
    return this.closest('[id^="shopify-section-"]') || document;
  }

  /** Load More with progress: only a press loads, nothing on scroll. */
  get pressOnly() {
    return this.hasAttribute('data-load-more-progress');
  }

  loadConfig() {
    const dataEl = this.scope.querySelector('[data-ia-collection-data]');
    if (!dataEl) return;

    try {
      const data = JSON.parse(dataEl.textContent);
      this.config.totalProducts = data.totalProducts || 0;
      this.config.productsPerPage = data.productsPerPage || 48;
      this.config.productsPerBatch = data.productsPerScroll || 12;
      // Always start from page 1, ignore URL page parameter
      this.config.currentPage = 1;
    } catch (e) {
      console.error('Failed to load collection data:', e);
    }
  }

  // === DOM HELPERS ===

  getProductGrid() {
    return this.scope.querySelector('[data-ia-product-grid]');
  }

  // === PRODUCT MAPPING ===

  mapProducts() {
    const grid = this.getProductGrid();
    if (!grid) return;

    const products = Array.from(grid.querySelectorAll('.ia-collection-grid__product'));
    this.pageItems = products.map(el => ({
      element: el,
      rendered: el.style.display !== 'none'
    }));
  }

  // === INTERSECTION OBSERVER ===

  setupObserver() {
    this.observer?.disconnect();

    const target = this.querySelector('.ia-collection__load-more-section');
    if (!target || this.pressOnly) return;

    // Only after a real scroll: the observer's first callback would otherwise
    // cascade several batches onto the first screen.
    if (!this._userScrolled) {
      window.addEventListener('scroll', () => { this._userScrolled = true; }, { once: true, passive: true });
    }

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && this._userScrolled) this.showNextBatch();
    }, { rootMargin: '200px', threshold: 0.1 });

    this.observer.observe(target);
  }

  showNextBatch() {
    const unrendered = this.pageItems.filter(item => !item.rendered);
    if (unrendered.length === 0) return;

    const batch = unrendered.slice(0, this.config.productsPerBatch);
    batch.forEach(item => {
      item.element.style.display = '';
      item.rendered = true;
    });
    // The slots judged by what sits below them (ia-grid-guard) measure again.
    publish(EVENTS.productsRendered, { detail: { revealed: batch.length } });

    this.updateUI();
  }

  // === UI UPDATES ===

  updateUI() {
    const done = this.updateButton();
    this.updateProgress(done);
  }

  /** Returns whether anything is left to load. */
  updateButton() {
    const section = this.querySelector('.ia-collection__load-more-section');
    if (!section) return true;

    const allRendered = this.pageItems.every(item => item.rendered);
    const hasNextPage = !!this.querySelector('[data-ia-pagination-container] a[aria-label="Next page"]');
    const done = allRendered && !hasNextPage;

    section.classList.remove('is-invisible');
    this.classList.remove('is-invisible');

    // The row hosts the observer, so it stands until nothing is left at all: a
    // row out of layout stops intersecting and the scroll reveal dies with it.
    // Whether a press would DO anything is the narrower question the idle mark
    // answers, and the stylesheet takes an idle row's button and its margins.
    // Press-only has no scroll reveal, so the button stands while anything is left.
    const persistent = this.pressOnly || this.hasAttribute('data-load-more-persistent');
    const pressDoes = persistent ? !done : (allRendered && hasNextPage);
    section.hidden = done;
    section.toggleAttribute('data-idle', !pressDoes);

    if (done) this.observer?.disconnect();
    return done;
  }

  /** Count and line follow the cells on screen; once nothing is left, the total
   *  is what showed (products the offer filter drops never get a cell). */
  updateProgress(done) {
    const progress = this.querySelector('.ia-collection__progress');
    const count = progress?.querySelector('[data-progress-count]');
    if (!count) return;

    const shown = this.pageItems.filter((item) => item.element.style.display !== 'none').length;
    const total = done ? shown : Math.max(shown, parseInt(progress.dataset.progressTotal, 10) || 0);
    progress.style.setProperty('--ia-load-progress', total ? shown / total : 1);

    const text = count.dataset.text.replaceAll('[shown]', shown).replaceAll('[total]', total);
    if (count.textContent !== text) count.textContent = text;
  }

  /** Spinner up, clicks off, the same pairing the cart and the search use.
   *  Held for LOAD_MORE_BUSY_MS at least: a reveal (and a cached page) can
   *  finish within a frame, and a flash that short reads as a glitch. */
  setLoadMoreBusy(isBusy) {
    const section = this.querySelector('.ia-collection__load-more-section');
    const button = this.querySelector('[data-ia-load-more-btn]');
    if (!section) return;

    clearTimeout(this._busyTimer);
    if (isBusy) {
      this._busySince = performance.now();
      section.classList.add('is-loading');
      if (button) button.disabled = true;
      return;
    }

    const left = LOAD_MORE_BUSY_MS - (performance.now() - (this._busySince || 0));
    const clear = () => {
      section.classList.remove('is-loading');
      if (button) button.disabled = false;
    };
    if (left > 0) this._busyTimer = setTimeout(clear, left);
    else clear();
  }

  // === NAVIGATION ===

  handlePageClick(event) {
    const loadMoreBtn = event.target.closest('[data-ia-load-more-btn]');
    if (loadMoreBtn) {
      event.preventDefault();
      // Cells of this page that are still hidden come first: the scroll
      // observer reveals them too, and the button stands for as long as either
      // of them is left, so it has to do both jobs.
      if (this.pageItems.some((item) => !item.rendered)) {
        this.showNextBatch();
      } else {
        this.navigateToNextPage();
      }
      return;
    }

    const navButton = event.target.closest('a.ia-pagination__nav');
    const pageButton = event.target.closest('a[data-page]');

    if (navButton) {
      event.preventDefault();
      event.stopPropagation();
      const isPrev = navButton.getAttribute('aria-label') === 'Previous page';
      isPrev ? this.navigateToPreviousPage() : this.navigateToNextPage();
    } else if (pageButton && !pageButton.classList.contains('active')) {
      event.preventDefault();
      event.stopPropagation();
      const targetPage = parseInt(pageButton.dataset.page);
      if (!isNaN(targetPage)) this.navigateToPage(targetPage);
    }
  }

  navigateToPage(page) {
    // Forward only: the engine's counter starts at page 1 and only grows.
    if (page > this.config.currentPage) {
      const pagesToLoad = page - this.config.currentPage;
      for (let i = 0; i < pagesToLoad; i++) {
        publish(EVENTS.loadMoreRequested, {});
      }
    }
  }

  navigateToPreviousPage() {
    // Previous page is a fresh answer for the current address (page 1 again).
    if (this.config.currentPage > 1) {
      publish(EVENTS.urlChanged, {});
    }
  }

  navigateToNextPage() {
    publish(EVENTS.loadMoreRequested, {});
  }

  // === AJAX UPDATES ===

  handleFiltersUpdate(event) {
    const { html, append } = event.detail;
    if (!html) return;

    this.updatePaginationHTML(html);
    // When appending, increment page. When replacing, always reset to page 1
    if (append) {
      this.config.currentPage = this.config.currentPage + 1;
    } else {
      this.config.currentPage = 1;
      this.pageItems = [];
    }

    setTimeout(() => {
      this.loadConfig();
      this.mapProducts();
      this.setupObserver();
      this.updateUI();
    }, 100);
  }

  updatePaginationHTML(html) {
    const parsedDom = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsedDom.querySelector('[data-ia-pagination-container]');
    const currentContainer = this.querySelector('[data-ia-pagination-container]');
    
    if (newContainer && currentContainer) {
      currentContainer.innerHTML = newContainer.innerHTML;
    }
  }
}

if (!customElements.get('ia-pagination-component')) {
  customElements.define('ia-pagination-component', IaPaginationComponent);
}

// Active Filters Component
class IaActiveFiltersComponent extends HTMLElement {
  constructor() {
    super();
    this._boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._boundHandleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick);
  }

  handleClick(event) {
    const button = event.target.closest('[data-remove-filter], [data-clear-filters]');
    if (button) {
      event.preventDefault();
      const url = button.dataset.removeFilter || button.dataset.clearFilters;
      if (url) {
        history.pushState({}, '', url);
      }
    }
  }
}

if (!customElements.get('ia-active-filters-component')) {
  customElements.define('ia-active-filters-component', IaActiveFiltersComponent);
}

// Single SOLID Filter Reset Component
class IaFilterReset extends HTMLElement {
  constructor() {
    super();
    this._boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    // Use capture phase to intercept before link default behavior
    this.addEventListener('click', this._boundHandleClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick, true);
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const link = event.target.closest('a, button');
    if (link) {
      const url = link.getAttribute('href') || link.dataset.resetUrl;
      if (url) {
        // Drawer stays open: filterCore intercepts pushState and re-fetches.
        history.pushState({}, '', url);
      }
    }
  }
}

if (!customElements.get('ia-filter-reset')) {
  customElements.define('ia-filter-reset', IaFilterReset);
}

// Apply Filters Button Component
class IaApplyFiltersButton extends HTMLElement {
  constructor() {
    super();
    this._boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._boundHandleClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick, true);
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const drawer = this.closest('ia-drawer-component');
    if (drawer) drawer.close();
    
    // Its own drawer's forms, not the first ones in the document: a second
    // drawer on the page must not have its state submitted by this button.
    const home = this.closest('[data-drawer-id]') || document;
    const forms = home.querySelectorAll('form[data-ia-filter-form], form[data-ia-sort-form]');
    publish(EVENTS.filtersApply, { detail: { forms: [...forms] } });
  }
}

if (!customElements.get('ia-apply-filters-button')) {
  customElements.define('ia-apply-filters-button', IaApplyFiltersButton);
}
